/**
 * Stable machine-readable error codes for `application/problem+json` (RFC 9457).
 *
 * `code` is the contract: the client maps it to an i18n key, so a code is never renamed and never
 * reused with a different meaning. `title`/`detail` in the response are for logs and developers —
 * they may change without a major version, `code` may not (stack.md, «Формат ошибок»).
 */
/** Base of the `type` URI. Not dereferenced at runtime; it identifies the problem class. */
export const PROBLEM_TYPE_BASE_URL = 'https://lawyer-broad-profile.dev/problems';
/**
 * Resources that can be missing, forbidden or duplicated. The list is a closed whitelist so that
 * `task_nto_found` is a compile error rather than a code the client silently fails to translate.
 * A resource is added here by the epic that introduces it, using its glossary name.
 */
export const ERROR_RESOURCES = [
    'organization',
    'team',
    'user',
    'role',
    'invitation',
    /**
     * A login session (`Session` in `data-model.md` §1), as addressed by
     * `DELETE /api/v1/auth/sessions/{sessionId}`. Its ids are enumerable and belong to one person, so
     * "not yours" is answered `session_not_found` rather than `session_forbidden` — the same rule
     * that hides entities of another organization (CLAUDE.md, invariant 2).
     */
    'session',
    /** Документ, поступивший в разбор: договор, положение, письмо (`units/document`). */
    'document',
    /** Строка настройки провайдера модели — админский ресурс (`units/ai-provider`). */
    'ai_provider',
    /** Прогон замера качества на наборе эталонных задач (`units/evaluation`). */
    'evaluation_run',
    /**
     * Хранимая трасса одного ответа ассистента (STORY-007-06) — одна строка на один `answerId`.
     *
     * Отвечает `answer_trace_not_found` и на несуществующий идентификатор, и на трассу другой
     * организации: внутри скоупа арендатора она не резолвится, и различать два случая значило бы
     * подтверждать, что у соседнего клиента такой ответ был (`CLAUDE.md`, инвариант 2 — то же
     * правило, по которому `session_not_found` отвечает «не твоя сессия»).
     */
    'answer_trace',
    /**
     * Разговор с ассистентом (EPIC-042) — тред вопросов и ответов, принадлежащий одному человеку.
     *
     * `assistant_thread_not_found` отвечает трём случаям сразу: разговора нет вовсе, разговор другой
     * организации, разговор коллеги. Третий случай — не небрежность, а решение: вопрос юриста сам по
     * себе бывает чувствительнее ответа, и подтверждать коллеге факт его существования не нужно.
     * Право `ai:read_any_thread` (в каталоге помечено `dangerous`) снимает третью границу, но не две
     * первые.
     */
    'assistant_thread',
    // ─── содержательные контексты юридической службы ───
    /** Локальный нормативный акт (`units/act` в глоссарии). */
    'act',
    /** Приказ. */
    'order',
    /** Ознакомление сотрудника с актом под подпись. */
    'acknowledgement',
    'contract',
    /** Плейбук — свод типовых условий, по которому проверяется договор. */
    'playbook',
    /** Лист согласования и его позиции. */
    'approval',
    /** Письмо — входящее или исходящее (`units/letter`); направление хранится полем, не типом. */
    'letter',
    /** Шаблон документа. */
    'template',
    /** Справочник целиком. */
    'reference',
    /** Запись справочника. */
    'reference_entry',
    'counterparty',
    /** Раздел базы знаний. */
    'kb_space',
    /** Статья базы знаний. */
    'kb_article',
    'file',
    'report',
    'dashboard',
];
/** Codes that are not about one resource, with the HTTP status each one is answered with. */
const GENERIC_ERROR_CODE_STATUS = {
    validation_failed: 422,
    unauthenticated: 401,
    /**
     * A credential was presented and refused: `POST /auth/login` with an email nobody has, with the
     * wrong password, or `POST /auth/change-password` with the wrong `currentPassword`.
     *
     * **One code covers "no such user" and "wrong password", and that is the point** (EPIC-006
     * acceptance). Two codes — or one code with two different `detail` strings — turn the login form
     * into an oracle that answers "does this person work here" for anybody with a word list, which is
     * the first step of every credential-stuffing campaign. The same requirement forbids branching on
     * anything else the caller can observe: status, body and elapsed time are equal in both cases,
     * so the use-case verifies a dummy hash when the user does not exist.
     *
     * Distinct from `unauthenticated` (no token, or an expired one) because the two mean opposite
     * things to the client: `unauthenticated` is answered by refreshing the session, and
     * `invalid_credentials` by telling the person their email or password is wrong.
     */
    invalid_credentials: 401,
    /**
     * The password was correct and the account is not allowed to open a session
     * (`User.status = SUSPENDED`). Reached only *after* a successful verification, so it discloses
     * nothing to somebody who does not already hold the password.
     */
    account_suspended: 403,
    /** Open registration is switched off for this installation and there is nobody to register. */
    registration_disabled: 403,
    /**
     * The password-reset token is unknown, already used, or expired — deliberately one code for all
     * three. Distinguishing them would tell a holder of a random token whether it ever existed, and
     * would let the "already used" answer confirm that somebody completed a reset.
     */
    password_reset_token_invalid: 400,
    /**
     * The invitation link is unknown, revoked, already accepted, expired, or belongs to an
     * organization that has been deactivated — **one code for all five**, and for the same reason as
     * the reset token above: telling them apart would let a holder of a guessed token learn whether it
     * ever existed, and would let «already accepted» confirm that a particular colleague joined
     * (`T-IAM-03`).
     *
     * `410 Gone` rather than 400: the link was a resource with a lifetime, and it is over. The client
     * cannot fix the request by changing it, which is exactly the distinction between 400 and 410 —
     * and the invitation screen shows «ask for a new link» rather than «check what you typed».
     */
    invitation_not_valid: 410,
    /**
     * The operation has to send mail and this installation has no `SMTP_URL`.
     *
     * Not `feature_disabled`: that one means the operator switched an optional subsystem off and the
     * honest answer is "this installation does not do that". Password recovery is not optional — the
     * operator wanted it and the deployment is incomplete, the person on the other end should be told
     * to contact an administrator rather than that the product lacks the feature, and the fix is a
     * configuration change rather than a purchase. Answering "success" without a mail is the failure
     * mode this code exists to prevent (EPIC-014).
     *
     * **Where it is decided matters as much as which code it is.** On `POST /auth/forgot-password`
     * the whole operation rests on the answer being the same for a registered address and an unknown
     * one; a 503 raised *after* the lookup would be the difference between them, and the code would
     * become the enumeration oracle the 202 was designed not to be. The check is therefore made
     * before the address is resolved — stated in the operation's own description too, because that is
     * where somebody implementing it will look.
     */
    mail_not_configured: 503,
    /**
     * The two transport-level refusals, decided before any resource is identified.
     *
     * `route_not_found` is deliberately *not* spelled as a `<resource>_not_found`: an unmatched path
     * belongs to no resource, and reusing a resource code here would make the client translate
     * "task not found" for a typo in the URL. `payload_too_large` is raised by the body parser at
     * 1 MB (rules/security.mdc, rule 14) — uploads go straight to S3 through a presigned URL, so a
     * request body of that size is a client defect rather than a file.
     */
    route_not_found: 404,
    payload_too_large: 413,
    /** Right present, vault key absent — the server could not help even if it wanted to. */
    vault_locked: 423,
    stale_version: 409,
    idempotency_key_reuse: 409,
    /**
     * The operation would leave the organization without an account nobody can take rights from.
     *
     * Two call sites, one condition: offboarding the owner (EPIC-016) and removing the owner role
     * from the last holder (EPIC-015). The name is the one both call sites already use — a second
     * code for the same state would make the client translate the same sentence twice.
     *
     * A conflict rather than a denial: the caller may do this and the subject exists, so neither 403 nor
     * 404 is true. The request cannot be satisfied in the current state, and one specific action changes
     * it — transfer ownership first (`domain/identity/access/owner-offboarding.policy.ts`).
     */
    last_owner_required: 409,
    /**
     * The three states a permission cannot argue with, raised by the access layer
     * (`domain/access/access.errors.ts`).
     *
     * Conflicts rather than denials, for the same reason `last_owner_required` above is one: the
     * caller may perform the action, the object exists, and the *current state* refuses. Answering
     * 403 would tell them to ask for a right they already hold.
     *
     * - `period_locked` — approved time and invoiced work are closed; a right does not reopen them.
     * - `self_lockout` — the change would remove the actor's own last way back in.
     * - `system_role_immutable` — the composition of a system role is code, not data.
     */
    period_locked: 409,
    self_lockout: 409,
    system_role_immutable: 409,
    /**
     * A DENY exception aimed at the owner of the organization.
     *
     * A conflict rather than a denial, like the ones above: the caller may write overrides and the
     * subject exists — the state is what refuses, because one such row makes the organization
     * unadministrable. Refused twice, by the use-case and by a trigger, so a direct SQL session cannot
     * write one either.
     */
    owner_immutable: 409,
    /**
     * The operation is allowed and deliberately not performed without a second signal.
     *
     * 428 rather than 403: nothing is missing and nothing is wrong — the caller has the right, and the
     * request would do something whose blast radius is larger than it looks (a role that grants
     * `user:impersonate`, a mass revocation). The client repeats it with the confirmation header, which
     * is why the status has to be one a client can act on rather than one it reports as a failure.
     */
    /** The invitation was accepted: a state conflict, not a missing row and not a refusal. */
    invitation_already_accepted: 409,
    /**
     * The proposed manager reports back to the person being edited. **422**, not 409: the conflict is
     * not with a stored state that somebody else changed — the request as written describes an
     * organization that cannot exist, and the fix is a different value in the field the client sent.
     */
    manager_cycle_detected: 422,
    /**
     * Termination before hiring. **422** for the same reason as the cycle above: the request is well
     * formed, and what it describes is a record that cannot exist.
     */
    employment_period_inverted: 422,
    /**
     * The person the organization would be handed to cannot hold it: suspended, or an invitation
     * nobody has accepted. **409**, not 422 — the request is well formed and the account exists; what
     * is wrong is its state, and the fix is to reactivate them first.
     */
    recipient_not_active: 409,
    member_not_active: 409,
    /**
     * Разговор с ассистентом достиг предела длины (`MAX_THREAD_MESSAGES`).
     *
     * **409, а не 422.** Запрос сформирован верно и вопрос сам по себе допустим; неверно состояние
     * разговора, и исправляется оно не правкой поля, а началом нового разговора. Ровно та же логика,
     * по которой `member_not_active` — конфликт, а не ошибка валидации.
     */
    assistant_thread_full: 409,
    /**
     * Действие «сверить редакцию» вызвано для документа, у которого не указаны официальные реквизиты
     * акта (EPIC-050, STORY-050-07, ADR-0015).
     *
     * **409, а не 422.** Запрос корректен и адресует существующий документ; неверно состояние
     * карточки — у неё нет того, без чего сверка не может опознать акт на портале. Исправляется не
     * правкой этого запроса, а отдельной операцией — `PUT /documents/{documentId}/official-act-reference`.
     *
     * Угадывать акт по наименованию документа запрещено умышленно: ошибка опознания дала бы пометку
     * «актуально» на чужом акте, а это хуже отсутствия пометки (ADR-0015, «Полнота сверки»).
     */
    official_act_reference_missing: 409,
    /**
     * Действие «сверить редакцию» вызвано для документа, чей вид акта не входит в закрытый перечень
     * видов, по которым существует официальный статус консолидированной редакции (EPIC-050,
     * STORY-050-07): законы и акты Президента (Указ Президента РФ от 03.03.2022 № 90).
     *
     * **409, а не 403 и не 422.** Право на действие есть, тело запроса ни при чём — отказывает
     * содержание карточки: вид акта, который сам по себе не поменять этим запросом. Дырявая сверка
     * хуже отсутствия сверки: показывать «актуально» по акту, для которого источник не даёт такой
     * гарантии, было бы ложным обещанием продукта (ADR-0015, рекомендация, условие 1).
     */
    edition_check_not_eligible_act_kind: 409,
    /**
     * Действие «сверить редакцию» вызвано для документа без принятой редакции: разбор ещё не дошёл
     * до записи, или файл не разобрался (EPIC-050, STORY-050-07).
     *
     * **409.** Отдельно от `official_act_reference_missing` — реквизиты акта и разбор файла суть
     * независимые состояния карточки. Без даты вступления в силу загруженной редакции сравнивать
     * найденную на портале дату не с чем: сравнение с датой подписания самого акта назвало бы
     * «опубликована новая редакция» первую же правку, которую документ когда-либо получал.
     */
    edition_check_no_loaded_revision: 409,
    /**
     * Handing the organization to oneself. **422**: the value in the field is wrong, and no state
     * anywhere would make it right.
     */
    invalid_recipient: 422,
    /**
     * `organization:transfer_ownership` was held — by role, by a per-user override, or by a custom
     * role — and the holder is not `organizations.owner_id`. **403**, inside the caller's own
     * organization: nothing here is a missing row, so `organization_not_found` would be false, and it
     * is not the generic `organization_forbidden` either, because that sentence says "you lack the
     * right" when the guard already confirmed the opposite. The one fact that decides this operation
     * is who the row currently names, and the code says exactly that (`domain/access/access.errors.ts`).
     */
    not_the_owner: 403,
    confirmation_required: 428,
    rate_limited: 429,
    /**
     * Исчерпан месячный предел расхода на обращения к модели.
     *
     * Отдельный код, а не `rate_limited`: статус тот же (429), а действие пользователя разное.
     * «Слишком часто» лечится ожиданием, «кончился бюджет» — только решением администратора, и
     * подсказка «повторите позже» здесь вводит в заблуждение до конца месяца.
     */
    ai_budget_exhausted: 429,
    /**
     * Четыре исхода обращения к модели, которые до сих пор были одним `service_unavailable`.
     *
     * **Почему четыре кода, а не один с четырьмя формулировками.** `service_unavailable` утверждает
     * ровно одно — «нужная служба сейчас недоступна, повторите позже», — и на живом стенде это
     * утверждение оказалось неверным дважды за один ответ: служба была доступна (не ответил
     * **провайдер модели**, а это разные вещи для того, кто решает, что делать дальше), а совет
     * повторить нельзя было исполнить, потому что повтор дал бы то же самое. Причины различает
     * контрольный запрос администратора с STORY-005-04; здесь тот же перечень доведён до ответа
     * пользователю (`domain/ai/ai-call-failure.enums.ts`).
     *
     * Разделяются они по **действию**, а не по красоте: подождать, позвать администратора чинить
     * ключ, позвать администратора чинить модель, сообщить о неисправности. Один код на четыре
     * случая заставляет выполнить неверное действие — ровно тот довод, по которому выше разведены
     * `rate_limited` и `ai_budget_exhausted`.
     *
     * Недоступность самого адреса провайдера осталась `service_unavailable` (503): это единственный
     * из пяти исходов, о котором «повторите позже» — правда.
     *
     * **Статусы шлюзовые, и это не украшение.** Наш сервер исправен; неисправен тот, к кому он
     * обратился, и HTTP различает эти случаи ровно тремя кодами: 502 — обратившийся ответил
     * непригодным, 503 — до него не достучались, 504 — не дождались. 501 не подходит («не
     * реализовано, повторять бессмысленно» — но реализовано и настроено, просто настроено неверно),
     * 500 солгал бы про дефект в нашем коде, а 401 запрещён отдельно: клиент отвечает на него
     * обновлением сессии и, при второй неудаче, выходом из системы
     * (`presentation/http/middleware/authenticate.middleware.ts`), то есть неверный ключ провайдера
     * выкидывал бы юриста из продукта.
     *
     * - `ai_provider_timeout` — модель не ответила за отведённое время. Единственный из четырёх, где
     *   повтор осмыслен: долгий ответ бывает разовым.
     * - `ai_provider_key_rejected` — провайдер не принял ключ. Повтор бесполезен до вмешательства
     *   администратора; пользователю сообщается именно это, а не «недоступно».
     * - `ai_model_not_available` — провайдер работает и не знает названной модели. Отдельно от ключа,
     *   потому что администратор чинит **другое поле**.
     * - `ai_answer_unreadable` — провайдер ответил, и ответ не разобрался: связь есть, ключ принят,
     *   модель нашлась, время уложилось. **Не про доступность вовсе**, и это единственная причина,
     *   по которой код существует отдельно, а не сведён к предыдущим: сервер уже повторил запрос сам
     *   (`ask-model.use-case.ts`), поэтому просить повторить ещё раз — предлагать человеку то, что
     *   за него уже сделали и что не помогло.
     */
    ai_provider_timeout: 504,
    ai_provider_key_rejected: 502,
    ai_model_not_available: 502,
    ai_answer_unreadable: 502,
    /**
     * Провайдера нельзя удалить: на него ссылается неизменяемая история.
     *
     * Журнал выходов за периметр и записи расхода хранятся пять лет по требованию аттестации и
     * ссылаются на провайдера с запретом каскада. Это не ошибка ввода (422) — введено всё верно,
     * и не отсутствие права: конфликт с состоянием, которое нельзя изменить по замыслу.
     */
    ai_provider_in_use: 409,
    /**
     * Адрес пункта неоднозначен: одинаковый номер есть и в основном тексте, и в приложении.
     *
     * Не 404: пункт как раз найден, и не один. Просить у пользователя полный путь честнее, чем
     * выбрать за него первый попавшийся — в юридическом документе это разные основания.
     */
    clause_address_ambiguous: 409,
    /**
     * Содержимое файла не разобралось на адресуемые пункты, и потому документом он не стал.
     *
     * Не `422`: неверны не поля запроса, а содержимое присланного файла, и родовой отказ валидации
     * клиент переводит фразой про «отмеченные поля» — человек ищет ошибку там, где её нет. Конфликт
     * состояния, а не ввода: тот же файл не примут и со второй попытки.
     */
    document_not_parsed: 409,
    /**
     * Исходного файла у документа нет — скачивать нечего.
     *
     * Не 404: документ существует, виден и читается предпросмотром; отсутствует только тело, из
     * которого он когда-то был принят. Два пути сюда, и оба — состояние, а не ошибка запроса:
     * документ принят до того, как система начала запоминать объект приёма (`upload_id` у таких строк
     * `NULL`), либо объект пропал из хранилища. Родовой 404 сказал бы «нет такого документа» и увёл бы
     * человека искать опечатку в ссылке, которая верна.
     */
    document_file_unavailable: 409,
    /**
     * Новая граница периода редакции пересекается с соседней редакцией того же документа.
     *
     * Конфликт состояния, а не ввода: сама дата корректна, несовместима она с тем, что уже записано.
     * Две редакции, действующие на один день, делают вопрос «какая редакция действовала на эту дату»
     * безответным — поэтому его отвергает ограничение исключения `ex_document_revisions_period` в
     * базе, а не проверка в коде, и этот код — то, во что отказ базы превращается наружу.
     */
    document_revision_period_conflict: 409,
    /**
     * Запрошенный переход статуса документа не разрешён картой (`rules/legal-documents.mdc`, п. 6).
     *
     * Конфликт состояния, а не ввода: `REVOKED` в теле запроса — законное значение, несовместимо оно с
     * тем статусом, в котором документ сейчас. Родовой `validation_failed` увёл бы человека проверять
     * поля, которые верны, а ответ обязан объяснить, что отменять уже отменённое нечем: восстановление
     * действия оформляется отдельным документом и новой редакцией, а не откатом статуса.
     */
    act_status_transition_not_allowed: 409,
    /**
     * Смена статуса без пригодного документа-основания.
     *
     * Ровно то поле, которое отвечает на главный вопрос заказчика — «чем установлено»
     * (`rules/legal-documents.mdc`, п. 7). Не 422: тело запроса разобрано, `basisDocumentId` в нём
     * есть — непригоден он **как основание**: пустой, либо это сам изменяемый документ. Документ,
     * утверждающий сам себя, не отвечает ни на один вопрос аттестации.
     */
    act_status_basis_required: 409,
    /**
     * Такая связь между этими двумя документами уже заведена.
     *
     * Одна запись — два представления (`rules/legal-documents.mdc`, п. 10): вторая строка «для
     * обратной ссылки» запрещена, потому что разойдётся с первой. Повтор — состояние, а не ошибка
     * ввода: связь существует и видна с обеих карточек, и заводить её заново нечего.
     *
     * Родовой `<resource>_already_exists` здесь не годится: связь не заведена в `ERROR_RESOURCES` как
     * самостоятельный ресурс — она не адресуется собственным маршрутом и живёт **под** документом.
     * Заводить ради неё ресурс значило бы опубликовать три кода, из которых нужны два.
     */
    act_link_already_exists: 409,
    /**
     * Под этим документом такой связи нет — её сняли, или идентификатор относится к чужой связи.
     *
     * Спеллинг `<something>_not_found` **без** ресурса в каталоге — тот же приём, что у
     * `route_not_found`, и по той же причине: связь не адресуется собственным маршрутом, она живёт под
     * документом, и права на неё — права на документ. Родовой `document_not_found` здесь сказал бы «нет
     * такого документа» человеку, который смотрит на его карточку, и увёл бы его искать опечатку в
     * ссылке, которая верна, — ровно тот довод, по которому заведён `document_file_unavailable`.
     */
    act_link_not_found: 404,
    /**
     * Названная редакция — не редакция того акта, под которым её направляют на ознакомление.
     *
     * **409, и это утверждение о паре, а не о редакции.** Ответ один и тот же на три случая: редакция
     * принадлежит другому документу, редакции не существует вовсе, редакция принадлежит соседнему
     * клиенту. Различать их значило бы сделать тело запроса оракулом существования редакций чужой
     * организации (`CLAUDE.md`, инвариант 2), а сказать `404` — увести юриста искать опечатку в
     * идентификаторе акта, который он открыл на экране и который верен.
     */
    acknowledgement_revision_mismatch: 409,
    /**
     * Редакцию нельзя направлять на ознакомление в том состоянии, в котором она сейчас.
     *
     * Проект правится **без** порождения новой редакции (`rules/legal-documents.mdc`, п. 2), поэтому
     * подпись под ним не отвечает на вопрос «с каким текстом ознакомился сотрудник» — а именно за этот
     * ответ модуль и куплен. Вытесненная редакция неизменяема, но уже не действует, и знакомить с ней
     * значит вводить человека в заблуждение о том, чем он обязан руководствоваться.
     *
     * Не 422: поле в теле законно и редакция существует — несовместима она с собственным состоянием.
     * Ранее собранные подписи под вытесненной редакцией этим кодом не затрагиваются: он стоит на пути
     * нового направления, а не на пути истории.
     */
    acknowledgement_revision_not_in_force: 409,
    /**
     * Прогоны замера несопоставимы — разные наборы задач либо разные версии разметки.
     *
     * Сравнение таких прогонов даёт число, которое выглядит осмысленным и не значит ничего;
     * поэтому отказ, а не сравнение с оговоркой.
     */
    evaluation_runs_not_comparable: 409,
    /**
     * Набора эталонных задач, на котором получен прогон, на этой установке нет.
     *
     * **404, и спеллинг `<something>_not_found` без ресурса в каталоге** — тот же приём, что у
     * `act_link_not_found`, и по той же причине: набор не адресуется собственными правами (его читает
     * тот же ключ `evaluation_run:read`), заводить ради него ресурс значило бы опубликовать три кода,
     * из которых нужен один.
     *
     * Отдельно от `evaluation_run_not_found`, и разница существенная. Прогон **есть** и виден; нет
     * разметки, по которой он посчитан, — а пересчитать его по другой разметке нельзя, это была бы
     * другая цифра под прежним именем (`application/evaluation/use-cases/summarize-run.query.ts`).
     * Родовой `evaluation_run_not_found` увёл бы читателя искать опечатку в идентификаторе прогона,
     * который он взял из вывода команды и который верен.
     *
     * Установка, где набор не настроен вовсе, отвечает `501 feature_disabled`: там не «нет этого
     * набора», там нет самой возможности, и это штатное состояние, а не отсутствие объекта.
     */
    evaluation_gold_set_not_found: 404,
    /**
     * `POST /auth/2fa/confirm` with a code that does not match the draft secret at any accepted step.
     *
     * One code for "wrong digits", "no draft to confirm" and "draft expired": telling them apart would
     * hand the caller a way to probe whether an enrolment is in progress on an account they do not
     * control, and — for the expiry case — how long ago it started (EPIC-017). **422**, not 401: the
     * caller is already authenticated, so nothing here is a credential failure — the field submitted
     * is what is wrong, the same distinction `manager_cycle_detected` draws.
     */
    invalid_totp_code: 422,
    /**
     * The presented TOTP code matches the step that was already accepted once.
     *
     * A separate code from `invalid_totp_code` on purpose, even though both are 422 and both come from
     * the same field: this one is reachable only by presenting a code that *was* correct, so the
     * anti-replay predicate (`totp_last_counter`) is the reason, not a typo — the distinction a person
     * watching over somebody's shoulder or replaying a captured value would produce, and the one the
     * client can tell apart in its copy ("that code was already used").
     */
    totp_code_replayed: 422,
    /**
     * `POST /auth/2fa/setup` when `totpEnabledAt` is already set.
     *
     * A conflict, not a validation failure: the request is well formed and the caller may in principle
     * set up 2FA — the *state* refuses, because a second enrolment would silently replace a working
     * secret with one nobody has scanned yet (EPIC-017). The existing secret is never re-read to
     * produce this answer — it stays exactly as unreadable as it is anywhere else.
     */
    mfa_already_enabled: 409,
    /**
     * A self-service action that changes or reveals a security-relevant secret was requested without a
     * fresh proof of the credentials that guard it.
     *
     * `POST /auth/2fa/recovery-codes/regenerate` is the one caller today: it requires the current
     * password **and** a live TOTP code in the same request, and refuses with this code — not
     * `invalid_credentials` and not `invalid_totp_code` — when either is missing or wrong, so the two
     * reasons are not distinguishable from outside (EPIC-017). **403**, not 401: the caller already
     * holds a valid session: what is missing is a second, fresher proof, not the first.
     */
    reauthentication_required: 403,
    /**
     * A recovery code was presented and refused: unknown, already used, or belonging to the wrong
     * account. One code and one body for all three, on the same reasoning `invalid_credentials`
     * documents above — telling them apart would let somebody holding a stolen or guessed code learn
     * whether it was ever real, or that a colleague had already used theirs (EPIC-017). **401**:
     * reached before any session exists, on the second-factor step of sign-in.
     */
    recovery_code_invalid: 401,
    /**
     * The three refusals of the second-factor step of sign-in (`POST /auth/2fa/verify`), all **401**.
     *
     * They exist as their own codes rather than reusing `invalid_totp_code` / `totp_code_replayed`
     * because those two are **422** and have to stay that way. A code carries exactly one status —
     * `AppError` derives it and refuses to take one from the caller
     * (`packages/server/src/domain/shared/errors/app.errors.ts`) — so one code cannot answer 422 on
     * `POST /auth/2fa/confirm` and 401 here. Both statuses are right for their own step: on `confirm`
     * the caller already holds a session and what is wrong is the field they submitted, while here no
     * session exists yet and the second factor *is* the credential being judged. That is the same
     * sentence `recovery_code_invalid` above already carries for the very same step.
     *
     * Moving the enrolment codes to 401 instead was rejected: the client's contract for 401 is
     * "refresh, and on a second 401 clear the session"
     * (`presentation/http/middleware/authenticate.middleware.ts`), so a mistyped digit while turning
     * 2FA on would sign the person out of the product (EPIC-017).
     *
     * - `mfa_token_expired` — the intermediate token is expired, unknown, already spent by a
     *   successful verification, or voided after five failed attempts: **one code for all four**, on
     *   the reasoning `password_reset_token_invalid` documents above. Telling them apart would say
     *   whether a particular sign-in was ever started and whether somebody finished it. The client
     *   answers it by returning to the password step.
     * - `mfa_invalid_code` — the second factor did not match. It hides nothing further on its own:
     *   reaching it at all requires holding a live intermediate token, which requires the password.
     * - `mfa_code_replayed` — the presented code matches the step already accepted once
     *   (`totp_last_counter`). Kept apart from `mfa_invalid_code` for the reason `totp_code_replayed`
     *   is kept apart from `invalid_totp_code`: this one is reachable only by presenting a code that
     *   *was* correct, so the client can say «that code was already used» rather than «check the app».
     */
    mfa_token_expired: 401,
    mfa_invalid_code: 401,
    mfa_code_replayed: 401,
    /**
     * An optional subsystem is switched off in this installation, and the honest answer is "this
     * installation does not do that": search without Meilisearch, the assistant with `AI_ENABLED`
     * off.
     *
     * **SMTP is deliberately not in that list** — it used to be, one line away from the paragraph on
     * `mail_not_configured` that says the opposite, which left the next reader to pick between two
     * statements of this file. The two codes differ in what they promise the caller, which is what
     * the status carries: 501 means the server does not implement this and retrying is pointless,
     * 503 means it would if it could and retrying after the operator acts is the correct behaviour.
     * Password recovery is not an optional feature; a deployment without `SMTP_URL` is incomplete.
     */
    feature_disabled: 501,
    /**
     * Материал нельзя обрабатывать провайдером, который считает вне периметра заказчика.
     *
     * Отдельный код, а не `*_forbidden`: отказ не про права пользователя — он может иметь полный
     * доступ к документу — и не про выключенную подсистему. Он про происхождение материала, и
     * пользователю надо сказать именно это, иначе единственное действие, которое он предпримет, —
     * попросит расширить себе права, что ничего не изменит.
     */
    external_processing_not_allowed: 403,
    /**
     * Общий вопрос задан провайдеру, который стоит **вне** периметра.
     *
     * Ветвь `GENERAL` отвечает без опоры на документы организации, и по построению не может нести
     * ссылку на пункт: ссылаться не на что. Но отсутствие материала не делает вопрос безопасным —
     * формулировка вопроса сама по себе может нести коммерческую тайну («вправе ли мы расторгнуть
     * договор с подрядчиком, который сорвал срок на объекте в Мурманске»). Поэтому общий режим
     * закрыт для внешнего провайдера, и отказ приходит **до** обращения к сети, а не после.
     *
     * `403`, а не `422`: запрос корректен, отвечает устройство контура, и повторять его в том же
     * контуре бессмысленно.
     */
    general_mode_unavailable: 403,
    /**
     * Общий вопрос задан в разговоре, где выбран материал.
     *
     * Выбранные документы — это заявление «спрашиваю про них». Ответить на такой вопрос в общем
     * режиме значило бы молча проигнорировать выбор человека и выдать рассуждение там, где ждали
     * вывода по своим документам, — причём внешне эти два ответа неразличимы.
     *
     * `409`, а не `422`: и вопрос, и разговор по отдельности корректны, несовместимы они друг с
     * другом. Снять материал или спросить по документам — оба выхода принадлежат человеку.
     */
    assistant_thread_material_selected: 409,
    /**
     * Уточнение перед составлением (EPIC-050, STORY-050-04) отвечено повторно, либо разговор
     * продолжился после него иначе.
     *
     * **409, а не 404.** Реплика `CLARIFICATION_REQUESTED` существует и видна — вызывающий смотрит на
     * неё в ленте секунду назад, — отвечать «нет такой реплики» значило бы отправить его искать
     * опечатку в верном идентификаторе. Неверно **состояние**: уточнение отвечается только пока оно
     * последняя реплика ленты, а к моменту повторного вызова это уже не так. Следующий шаг —
     * перечитать разговор и посмотреть, что там уже есть, а не повторить тот же запрос.
     */
    assistant_clarification_already_answered: 409,
    /**
     * Правка содержимого версии шаблона, которая уже утверждена (EPIC-034, STORY-034-04).
     *
     * 409, а не 403: право у вызывающего есть, отказывает **состояние** — по этой версии собраны
     * документы, и правка на месте изменила бы задним числом то, чем они были собраны. Свой код, а не
     * `stale_version`: тот означает «твоя копия устарела, перезагрузи», и клиент показывает по нему
     * предложение перезагрузить страницу. Здесь перезагрузка не поможет — следующий шаг другой:
     * породить новый черновик из этой версии. Разные следующие шаги — разные коды, иначе интерфейс
     * подсказывает не то действие.
     */
    template_version_immutable: 409,
    /**
     * По этой печатной форме нельзя собрать документ **сейчас**: она выведена из обращения либо не
     * имеет ни одной утверждённой версии (EPIC-034, STORY-034-08).
     *
     * 409, а не 404: форма существует, видна в перечне и открывается — отвечать «нет такой формы»
     * человеку, который на неё смотрит, значило бы отправить его искать опечатку в верной ссылке. Один
     * код на оба состояния, потому что следующий шаг у них один и тот же — утвердить версию или
     * вернуть форму в обращение, — и оба видны на её карточке.
     */
    template_not_assemblable: 409,
    /** A dependency needed to answer is unavailable — including "could not resolve the ACL". */
    service_unavailable: 503,
    internal_error: 500,
};
/**
 * Per-resource suffixes.
 *
 * `not_found` covers both "does not exist" and "belongs to another tenant": answering 403 there
 * would turn the API into an oracle of what exists in other organizations (invariant 2 in
 * CLAUDE.md). 403 is reserved for a denial *inside* the caller's own organization.
 */
const RESOURCE_ERROR_SUFFIX_STATUS = {
    not_found: 404,
    forbidden: 403,
    already_exists: 409,
};
const genericCodes = Object.keys(GENERIC_ERROR_CODE_STATUS);
const resourceSuffixes = Object.keys(RESOURCE_ERROR_SUFFIX_STATUS);
export const ERROR_CODES = [
    ...genericCodes,
    ...ERROR_RESOURCES.flatMap((resource) => resourceSuffixes.map((suffix) => `${resource}_${suffix}`)),
];
export const ERROR_CODE_STATUS = {
    ...GENERIC_ERROR_CODE_STATUS,
    ...Object.fromEntries(ERROR_RESOURCES.flatMap((resource) => resourceSuffixes.map((suffix) => [`${resource}_${suffix}`, RESOURCE_ERROR_SUFFIX_STATUS[suffix]]))),
};
const ERROR_CODE_SET = new Set(ERROR_CODES);
export const isErrorCode = (value) => ERROR_CODE_SET.has(value);
export const errorCodeStatus = (code) => ERROR_CODE_STATUS[code];
/** `validation_failed` → `https://lawyer-broad-profile.dev/problems/validation-failed`. */
export const problemTypeUrl = (code) => `${PROBLEM_TYPE_BASE_URL}/${code.replaceAll('_', '-')}`;
//# sourceMappingURL=error-code.enums.js.map